/**
 * MobileMenu Component - Simple Slide-out Menu
 *
 * Features:
 * - Hamburger button transforms to X in same position
 * - Slides in from right side
 * - Tap outside to close
 * - Swipe right-to-left to close
 * - Uses shared hooks for better code reusability
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import MobileAccordion from "./MobileAccordion";
import NavigationItem from "./NavigationItem";
import AudiatedLogo from "./AudiatedLogo";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useTouchGesture } from "../hooks/useTouchGesture";
import { MobileMenuProps } from "../types/navigation";
import styles from "../styles/MobileMenu.module.css";

export default function MobileMenu(props: MobileMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const drawerRef = useRef<HTMLDivElement>(null);

    // Use shared hooks
    useBodyScrollLock(isOpen);

    // Reset scroll position when menu opens
    useEffect(() => {
        if (isOpen && drawerRef.current) {
            drawerRef.current.scrollTop = 0;
        }
    }, [isOpen]);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
    }, []);

    const touchHandlers = useTouchGesture({
        threshold: 50,
        onSwipeRight: closeMenu,
    });

    // Close menu when route changes
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    const toggleMenu = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    return (
        <>
            {/* Hamburger/X Button */}
            <button
                className={`${styles.hamburger} ${isOpen ? styles.open : ""}`}
                onClick={toggleMenu}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
            >
                <span></span>
                <span></span>
                <span></span>
            </button>

            {/* Backdrop */}
            {isOpen && (
                <div
                    className={styles.backdrop}
                    onClick={closeMenu}
                    aria-hidden="true"
                />
            )}

            {/* Slide-in Drawer */}
            <aside
                ref={drawerRef}
                className={`${styles.drawer} ${isOpen ? styles.open : ""}`}
                {...touchHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button inside drawer - same position as hamburger */}
                <button
                    className={styles.closeButton}
                    onClick={closeMenu}
                    aria-label="Close menu"
                >
                    <span></span>
                    <span></span>
                </button>

                <div className={styles.menuContent}>
                    {/* Home Link */}
                    <NavigationItem
                        to={props.homeRoute || "/"}
                        label="Home"
                        icon="🏠"
                        variant="menu"
                        end
                    />

                    {/* Learn Section - Accordion */}
                    {props.learnItems && props.learnItems.length > 0 && (
                        <MobileAccordion label="Learn" icon="📚" items={props.learnItems} menuOpen={isOpen} />
                    )}

                    {/* Drill Section - Accordion */}
                    {props.drillItems && props.drillItems.length > 0 && (
                        <MobileAccordion label="Drill" icon="🎯" items={props.drillItems} menuOpen={isOpen} />
                    )}

                    {/* Demos Section - Accordion */}
                    {props.demoItems && props.demoItems.length > 0 && (
                        <MobileAccordion label="Demos" icon="🎨" items={props.demoItems} menuOpen={isOpen} />
                    )}

                    {/* Spotify Link */}
                    {props.spotifyRoute && (
                        <NavigationItem
                            to={props.spotifyRoute}
                            label="Spotify"
                            icon="🎵"
                            variant="menu"
                        />
                    )}

                    {/* Decorative Footer Section */}
                    <div className={styles.menuFooter}>
                        <div className={styles.floatingNotes}>
                            <span className={styles.note}>♪</span>
                            <span className={styles.note}>♫</span>
                            <span className={styles.note}>♬</span>
                            <span className={styles.note}>♩</span>
                            <span className={styles.note}>♭</span>
                            <span className={styles.note}>♯</span>
                            <span className={styles.note}>𝄞</span>
                            <span className={styles.note}>♪</span>
                            <span className={styles.note}>♫</span>
                            <span className={styles.note}>♬</span>
                        </div>
                        <div className={styles.tipSection}>
                            <div className={styles.tipIcon}>💡</div>
                            <div className={styles.tipText}>
                                <strong>Tip:</strong> Practice a little every day for the best results!
                            </div>
                        </div>
                        <Link to={props.homeRoute || '/'} className={styles.branding}>
                            <AudiatedLogo variant="mobile" />
                        </Link>
                    </div>
                </div>
            </aside>
        </>
    );
}
